import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { getNormalizedBaseUrl } from './utils';
import { toApiErrorMessage } from './error';
import { config as appConfig } from '@/config';
import { toast } from 'sonner';
import { GenericApiResponse } from '../common/common.types';

let adpApiClient: AxiosInstance | null = null;

function createApiClient(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 15000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use((req) => {
    if (appConfig.adpTenantName) {
      req.headers['x-tenant-name'] = appConfig.adpTenantName;
    }

    return req;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const message = toApiErrorMessage(error, 'Something went wrong');
      toast.error(message);
      return Promise.reject(new Error(message));
    }
  );

  return instance;
}

export function getAdpApiClient(): AxiosInstance {
  if (adpApiClient) return adpApiClient;

  const normalizedBaseUrl = getNormalizedBaseUrl(appConfig.adpApiUrl);

  if (!normalizedBaseUrl) {
    throw new Error('API URL is not configured');
  }

  adpApiClient = createApiClient(normalizedBaseUrl);

  return adpApiClient;
}

export async function apiRequest<T>(
  requestConfig: AxiosRequestConfig,
  fallbackMessage = 'API request failed'
): Promise<T> {
  try {
    const response = await getAdpApiClient().request<GenericApiResponse<T>>(requestConfig);

    const res = response.data;

    if (!res.success) {
      throw new Error(res.message || fallbackMessage);
    }

    return res.data as T;
  } catch (error) {
    throw new Error(toApiErrorMessage(error, fallbackMessage));
  }
}
